import { useQuery } from 'react-query';
import { ContractChangeOrderAssemblyService as ccoas } from '../api/ContractChangeOrderAssemblyService';
import { ProjectEstimateAssemblyService as peas } from '../api/ProjectEstimateAssemblyService';
import { useUnityBuildStore } from '../stores/store';
import { useStore } from 'zustand';
import { ObjectService } from '../api/ObjectService';
import { useMemo } from 'react';

export function useFetchCountedAssemblies() {
  const { selectedEstimate, selectedChangeOrder, selectedObject, currentPage } =
    useStore(useUnityBuildStore);

  const idForKey = selectedEstimate?.estimateid || selectedChangeOrder?.changeorderid;

  // Fetch the raw assembly data (only refetches when estimate/changeorder changes)
  const {
    data: rawAssemblies,
    error,
    isLoading,
    dataUpdatedAt,
  } = useQuery(
    ['countedAssemblies', idForKey],
    async () => {
      if (selectedEstimate?.estimateid && selectedChangeOrder?.changeorderid) {
        console.error(
          `Both selectedEstimateId ${selectedEstimate.estimateid} and selectedChangeOrderId ${selectedChangeOrder.changeorderid} are selected, only one should be selected`
        );
        return [];
      }

      let result;
      if (selectedEstimate?.estimateid) {
        const response = await peas.getEstimateAssembliesByEstimateId(selectedEstimate.estimateid);
        result = response.takeoffassembliesList;
      } else if (selectedChangeOrder?.changeorderid) {
        const response = await ccoas.getChangeOrderAssembliesByChangeOrderId(
          selectedChangeOrder.changeorderid
        );
        result = response.takeoffassembliesList;
      } else {
        return [];
      }
      return result;
    },
    {
      enabled: !!selectedEstimate?.estimateid || !!selectedChangeOrder?.changeorderid,
      // Keep previous data during refetch to avoid UI flicker
      keepPreviousData: true,
      // Cache for a short time so invalidation doesn't drop data immediately
      // We'll still refetch immediately, but UI keeps last snapshot during the request
      staleTime: 200, // slight staleness window
      cacheTime: 5 * 60 * 1000, // retain cache to prevent momentary nulls
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
    }
  );

  // Filter assemblies reactively when selectedObject or currentPage changes
  const data = useMemo(() => {
    if (!rawAssemblies || rawAssemblies.length === 0) {
      return {
        filteredAssemblies: [],
        unfilteredAssemblies: [],
      };
    }

    const filteredAssemblies = ObjectService.filterAssemblyByObjectIdandPageNumber(
      rawAssemblies,
      selectedObject?.id ?? '',
      currentPage
    );

    return {
      // Maintain backward compatibility: wrap filtered assemblies in the expected structure
      filteredAssemblies:
        filteredAssemblies && filteredAssemblies.length > 0 ? filteredAssemblies : [],
      unfilteredAssemblies: rawAssemblies,
    };
  }, [rawAssemblies, selectedObject?.id, currentPage]);

  return { data, error, isLoading, dataUpdatedAt };
}

export function useFetchAssemblyStats() {
  const { selectedEstimate, selectedChangeOrder } = useStore(useUnityBuildStore);

  const idForKey = selectedEstimate?.estimateid || selectedChangeOrder?.changeorderid;

  const queryKey = ['assemblyStats', idForKey];

  const { data, error, isLoading } = useQuery(
    queryKey,
    async () => {
      if (selectedEstimate?.estimateid && selectedChangeOrder?.changeorderid) {
        console.error(
          `Both selectedEstimateId ${selectedEstimate.estimateid} and selectedChangeOrderId ${selectedChangeOrder.changeorderid} are selected, only one should be selected`
        );
        return;
      }
      if (selectedEstimate?.estimateid) {
        const response = await peas.getEstimateAssembliesByEstimateId(selectedEstimate.estimateid);
        return response.takeoffassembliesList;
      } else if (selectedChangeOrder?.changeorderid) {
        const response = await ccoas.getChangeOrderAssembliesByChangeOrderId(
          selectedChangeOrder.changeorderid
        );
        return response.takeoffassembliesList;
      }
      throw new Error('No valid ID available for fetching countedAssemblies');
    },
    {
      enabled: !!selectedEstimate?.estimateid || !!selectedChangeOrder?.changeorderid,
      staleTime: 10000, // Consider data fresh for 10 seconds
      cacheTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );
  return { data, error, isLoading };
}
